/**
 * Custom IVR — wire format types.
 *
 * The on-the-wire JSON shape is consumed by the Asterisk dialplan and MUST
 * be preserved byte-for-byte across the legacy → new front-end migration.
 * Everything in this file mirrors the structure produced by
 * `obd_frontend/Helpers/utils.transformNodes` and accepted by the backend
 * at `POST /api/obd/add/custom/ivr` / `POST /api/obd/update/custom/ivr`.
 *
 * Keep this file as a strict-typed contract:
 * - Field names are wire-significant — never rename.
 * - Optional fields stay optional (some are written only by older flows).
 * - The opaque `flowRender` is the entire React-Flow Node object; both
 *   `react-flow-renderer@10` and `@xyflow/react@12` share the same Node
 *   shape (`{id, type, position, data}`), so swapping the library is
 *   transparent to the backend.
 */

/** Node verbs (the value stored in `node.data.verb` and on `flowJson.verb`). */
export type IvrVerb = 'answer' | 'play' | 'dtmf' | 'gather' | 'transfer' | 'record' | 'hangup';

/** React-Flow `node.type` field — drives the rendered node component. */
export type IvrNodeType =
  | 'answerNode'
  | 'playNode'
  | 'gatherNode' // legacy: gather covers both DTMF + gather verbs
  | 'transferNode'
  | 'recordNode'
  | 'hangupNode';

/* ---------------- Audio / action shapes ---------------- */

/** Audio metadata captured when the user picks a prompt. */
export interface IvrAudio {
  id?: number | string;
  audio?: string; // URL
  name?: string; // file name
  duration?: number;
}

export interface PlayAction extends IvrAudio {
  type?: 'play';
}

export interface RecordAction extends IvrAudio {
  type?: 'record';
}

export interface TransferAction {
  /** `'transfer_group'` is the only currently-supported transfer. */
  type?: 'transfer_group' | string;
  id?: number;
  name?: string;
}

/** DTMF / gather action carries multiple optional audios. */
export interface DtmfAction {
  menu?: IvrAudio;
  welcome?: IvrAudio | null;
  noInput?: IvrAudio | null;
  wrongInput?: IvrAudio | null;
}

/* ---------------- TTS / SMS sub-data ---------------- */

export interface IvrTtsRow {
  ttsType: 'prompt' | 'variable' | string;
  value: string;
  variableType?: string;
}

/** SMS payload bound to a specific DTMF key (key is `DTMF_0`, `DTMF_1`, …). */
export interface IvrSmsBinding {
  payload?: string;
  id?: string | number;
}

export type IvrSmsData = Record<string, IvrSmsBinding>;

/* ---------------- Per-node `data` payload (React-Flow node.data) ---------------- */

export interface IvrNodeData {
  label: string;
  verb: IvrVerb;
  /** Owner of the IVR — preserved so per-node fetches can include userId. */
  userId?: number | string;
  /** Optional API base — same reason. */
  apiPath?: string;

  /** Parent label (set when an upstream edge points here). */
  parent?: string;
  /** Used by gather children to clear their parent handle on remove. */
  idToRemove?: string;

  /* Play / Record */
  action?: PlayAction | RecordAction | TransferAction | DtmfAction;
  record_duration?: number | string;

  /* DTMF / gather */
  type?: string;
  numDigits?: number | string;
  timeout?: number | string;
  numSources?: number[];
  rePrompt?: number | string;
  ttsRows?: IvrTtsRow[];
  minValue?: string;
  manualDigits?: string;
  smsData?: IvrSmsData;
  /** Map of `${nodeId}_handle-${digit}` → {type, position, left}. */
  selectedHandles?: Record<
    string,
    { type: 'source'; position: 'bottom'; left: number }
  >;

  // Index signature for RF compatibility — RF requires node.data extends
  // `Record<string, unknown>`. We keep the typed fields above for ergonomics.
  [key: string]: unknown;
}

/** The actual React-Flow node shape we store (and round-trip through `flowRender`). */
export interface IvrNode {
  id: string;
  type: IvrNodeType;
  position: { x: number; y: number };
  data: IvrNodeData;
  /** RF may add extra runtime fields; preserve via index-signature on save. */
  [key: string]: unknown;
}

/** React-Flow edge — legacy stores it directly. */
export interface IvrEdge {
  id: string;
  source: string;
  target: string;
  sourceHandle?: string | null;
  targetHandle?: string | null;
  type?: string;
  label?: string;
  labelStyle?: Record<string, unknown>;
  data?: Record<string, unknown>;
  /** Whatever RF wrote for `markerEnd` — we don't introspect it on save. */
  markerEnd?: string | Record<string, unknown>;
}

/* ---------------- Transformed (wire) flow control + json ---------------- */

export interface IvrFlowControlNext {
  flowName: string;
  nextNodeId: number;
  isLoop?: boolean;
  parent: string;
}

export interface IvrFlowControl {
  default?: IvrFlowControlNext;
  /** `dtmf_0`, `dtmf_1`, … for gather nodes with multiple branches. */
  [dtmfKey: string]: IvrFlowControlNext | undefined;
}

export interface IvrFlowJson {
  verb: IvrVerb;
  action?: PlayAction | RecordAction | TransferAction | DtmfAction;
  duration?: number;
  record_duration?: number | string;
  type?: string;
  numDigits?: number | string;
  timeout?: number | string;
  numSources?: number[];
  rePrompt?: number | string;
  ttsRows?: IvrTtsRow[];
  minValue?: string;
  manualDigits?: string;
  smsData?: IvrSmsData;
}

export interface IvrTransformedNode {
  id: number;
  flowJson: IvrFlowJson;
  flowRender: IvrNode;
  flowControl: IvrFlowControl;
}

export interface IvrScheduleData {
  startTime?: string;
  endTime?: string;
  weekDays?: string[];
  isWholeDay?: boolean;
  isWholeWeek?: boolean;
}

/**
 * Final wire payload for `nodeData` (always JSON-stringified before send).
 * Mirrors `transformNodes` output exactly.
 */
export interface IvrWireData {
  flowName: string;
  userId: number | string;
  nodes: Record<number, IvrTransformedNode>;
  scheduleData?: IvrScheduleData;
  isGatherNode: number;
  promptDuration: number;
  smsWebhookFlag: boolean;
}

/* ---------------- API request / response shapes ---------------- */

export interface IvrComposeRequest {
  /** JSON.stringify(IvrWireData). */
  nodeData: string;
  /** JSON.stringify(IvrEdge[]). */
  edgesData: string;
  flowName: string;
  userId: number;
  is_gather_node: number;
  promptDuration: number;
}

export interface IvrUpdateRequest extends IvrComposeRequest {
  /** Path param for update — also echoed inside the body in legacy. */
  ivrId: number;
}

/** GET `/api/obd/get/custom/ivr/{id}` response. */
export interface IvrDetailResponse {
  id?: number;
  ivrId?: number;
  flowName: string;
  /** Stored as JSON-string of `IvrWireData`. */
  nodeData: string;
  /** Stored as JSON-string of `IvrEdge[]`. */
  edgesData: string;
  is_gather_node?: number;
  scheduleData?: IvrScheduleData;
  updationLogs?: Array<{ updationAt?: string }>;
}

/** GET `/api/obd/custom/ivr/list/{userId}` row. */
export interface IvrListRow {
  id: number;
  flowName: string;
  context?: string;
  createdAt?: string;
  updatedAt?: string;
  status?: string | number;
}

/** `/api/obd/ivr/settings` row — drives the verb palette. */
export interface IvrSettingRow {
  action: IvrVerb;
  name: string;
}

/* ---------------- Constants ---------------- */

/**
 * DTMF key order — `DTMF_0` …`DTMF_9`, `*`, `#`. Drives the per-key sort
 * on gather-node edges so the wire output is deterministic.
 */
export const DTMF_KEYS = [
  '0', '1', '2', '3', '4', '5', '6', '7', '8', '9', '*', '#',
] as const;

/** Default render order for the verb palette. */
export const IVR_SETTING_ORDER: IvrVerb[] = [
  'answer',
  'play',
  'gather',
  'transfer',
  'record',
  'hangup',
];

/** Tailwind-friendly accent colors keyed by verb. */
export const VERB_COLOR: Record<
  IvrVerb,
  {
    ring: string;
    bg: string;
    bgSoft: string;
    fg: string;
    fgStrong: string;
    chip: string;
  }
> = {
  answer: {
    ring: 'ring-emerald-300',
    bg: 'bg-emerald-500',
    bgSoft: 'bg-emerald-50',
    fg: 'text-emerald-900',
    fgStrong: 'text-emerald-600',
    chip: 'bg-emerald-50 border-emerald-300',
  },
  play: {
    ring: 'ring-sky-300',
    bg: 'bg-sky-500',
    bgSoft: 'bg-amber-50',
    fg: 'text-sky-900',
    fgStrong: 'text-amber-600',
    chip: 'bg-sky-50 border-sky-300',
  },
  dtmf: {
    ring: 'ring-violet-300',
    bg: 'bg-violet-500',
    bgSoft: 'bg-blue-50',
    fg: 'text-violet-900',
    fgStrong: 'text-blue-600',
    chip: 'bg-violet-50 border-violet-300',
  },
  gather: {
    ring: 'ring-violet-300',
    bg: 'bg-violet-500',
    bgSoft: 'bg-blue-50',
    fg: 'text-violet-900',
    fgStrong: 'text-blue-600',
    chip: 'bg-violet-50 border-violet-300',
  },
  transfer: {
    ring: 'ring-amber-300',
    bg: 'bg-amber-500',
    bgSoft: 'bg-emerald-50',
    fg: 'text-amber-900',
    fgStrong: 'text-emerald-600',
    chip: 'bg-amber-50 border-amber-300',
  },
  record: {
    ring: 'ring-rose-300',
    bg: 'bg-rose-500',
    bgSoft: 'bg-rose-50',
    fg: 'text-rose-900',
    fgStrong: 'text-rose-600',
    chip: 'bg-rose-50 border-rose-300',
  },
  hangup: {
    ring: 'ring-slate-300',
    bg: 'bg-slate-500',
    bgSoft: 'bg-rose-50',
    fg: 'text-slate-900',
    fgStrong: 'text-rose-600',
    chip: 'bg-slate-50 border-slate-300',
  },
};
